import { defineStore } from 'pinia'
import { useNotification } from "@kyvg/vue3-notification";
import { openWindowWithPost, readableFileSize, setObjectProperty } from "../helpers";
import moment from 'moment';
import axios from 'axios';
import { uniq } from 'lodash-es';
import { wTrans } from 'laravel-vue-i18n';

const { notify } = useNotification()

// Create a new store instance.
export const useAppStore = defineStore('app', {
  state: () => ({
    user: {},
    teamData: {},
    configs: false,
    emptyStateData: [],
    configurationLibrary: null,
    php_versions: [],
    wp_versions: [],
    default_php: null,
    default_wp: null,
    notification: {},
    warning: {},
    modal: {},
    refresh_table: false,
    ui: {
      popup_inside_modal: false,
      edit_custom_plan: false,
    },
    coupon_applied: false,
    coupon: {
      name: null,
      coupon_code: null,
      discount_value: null,
    },
    public_templates: [],
    user_templates: [],
    launch_site_status: false,
    launch_site_message: false,
    launch_site_progress: 0,
    launch_site_detail: {},
    launch_site_custom_redirect: false,
    coupon_banner_text: false,
    coupon_banner_link: false,
    callback: {},
    hosting_provider_site_installation_string: '',
    refresh_template_table: false,
    progress_template_table: {},
    upgrade_warning: {},
    feature_available_from: false,
    hosting_provider_site_list_string: "",
    hosting_provider_meta: null,
    domains: null,
    install_content_status: false,
    install_content_message: false,
    apply_wp_config_status: false,
    server_groups: [],
    new_version: false,
    site: {},
    site_task_id: null,
    commands: [],
    blogs: [],
    siteUsage: {},
    userMailTrapIntegrations: [],
    userIntegrationResources: [],
    selectedPlan: null,
    cardIsActivated: false,
    cardAdded: false,
    message: {},
    accept: null,
    link: null,
    stackcategoryList: [],
    storecategoryList: [],
    pageBuilders: [],
    upgradeFreeToPaid: {},
    plans: [],
    featureAvailableFromIntervalWise: [],
    paymentSuccessModal: {},
    refresh_dashboard_counts: true,
    tenant: {},
    connectBulkSelection: JSON.parse(localStorage.getItem('connectBulkSelection') ?? '[]'),
    selectAllConnectBulkEdit: JSON.parse(localStorage.getItem('selectAllConnectBulkEdit') ?? false),
    offers: [],
    currency: '$',
    atarimIntegration: null,
    suffixDoamins: [],
    generalSurveys: [],
    feedbackSurveys: [],
    feedbackSurveyEnabled: false,
    toggleNewTemplate: false,
    user_live_sites_count: 0,
    aiProviders: null,
    hostingConnections: null,
    wpCloudHostingConnections: null,
    preferredHostingProviders: [],
    otherHostingProviders: [],
    hostingProviderPacks: [],
    instaWpLiveOptions: {
      php_versions: [],
      data_centers: []
    },
    tagItems: [],
    ppuPlanMigrateModal: false,
    sitePlan: null,
    stripeProductPriceId: null,
    selectedSiteForPpuPlanMigrate: null,
    selectedPlanId: null,
    site_plans: [],
    free_site_count: 0,
    active_free_site_limit: 0,
    free_lifetime_site_count: 0,
    life_time_free_site_limit: 0,
    planUpgradeType: null,
  }),
  getters: {
    hideFreeSite: (state) => state.free_site_count > 3,
  },
  actions: {
    incrementFreeSiteCount() {
      this.free_site_count++;
    },
    hardRefresh() {
      this.new_version = false;
    },
    getHostingProviderSiteList(value) {
      this.hosting_provider_site_list_string = value;
    },
    getSiteInstallationString(value) {
      this.hosting_provider_site_installation_string = value;
    },
    closewindow(value) {
      this.callback = value;
    },
    setAuthUser(data) {
      this.user = data;
    },
    setTeamData(data){
      this.teamData = data;
    },
    setTenant(data) {
      this.tenant = data;
    },
    setCardAdded(data) {
      this.cardAdded = data;
    },
    userLogout() {
      this.user = {};
      this.teamData = {};
    },
    clearSite() {
      this.site = null;
    },
    async getSiteDetails(siteId){
      let response = await axios.get('/api/v2/sites/' + siteId + '/details');
        this.site = response.data.data;
        this.sitePlan = response.data.data.site_plan;
        this.incrementFreeSiteCount();
        return response.data.data;
    },
    async getHostingProvidersOptions(force = false) {
      if (!force && this.hostingProviderPacks.length > 0) {
        return
      }
      await axios
        .get("/api/v2/hosting-providers/packs")
        .then((response) => {
          this.hostingProviderPacks = response.data.data.packs;
          this.instaWpLiveOptions.php_versions = response.data.data.php_versions;
          this.instaWpLiveOptions.data_centers = response.data.data.data_centers;
        })
        .catch((error) => {
          const message = {
            heading: wTrans('failed'),
            subHeading: error?.response?.data.message || error.message,
            type: "error",
          };
          this.setNotification(message);
        })
    },
    async getHostingProviders(force = false) {
      if (!force && this.preferredHostingProviders.length > 0 && this.otherHostingProviders.length > 0) {
        return
      }
      await axios
        .get("/api/v2/hosting-providers")
        .then((response) => {
          this.preferredHostingProviders = response.data.data.preferred;
          this.otherHostingProviders = response.data.data.others;
        })
        .catch((error) => {
          const message = {
            heading: wTrans('failed'),
            subHeading: error?.response?.data.message || error.message,
            type: "error",
          };
          this.setNotification(message);
        });
    },
    async getHostingProviderMeta(hosting_provider_id) {
      const response = await axios.get(`/api/v2/hosting-providers/${hosting_provider_id}/meta`);
      this.hosting_provider_meta = response.data.data
      return true;
    },
    async getSubDomainList() {
      await axios
        .get("/api/v2/hosting-providers/subdomain-list")
        .then((response) => {
          this.domains = response.data.data;
          Object.keys(this.domains).forEach((key, index) => {
            this.domains[key] = '.' + this.domains[key];
          });
        }).catch(err => {

          if (err.response?.data?.message === "Please Setup and Verify your Suffix Domain.") {
            this.$inertia.visit('/whitelabel-domain-page');
          }
        });
    },
    async getHostingConnections(force = false) {
      if (!force && this.hostingConnections != null && this.wpCloudHostingConnections != null) {
        return
      }
      await axios.get('/api/v2/hosting-connections')
        .then(res => {
          this.hostingConnections = res.data.data.filter(item => item.provider?.slug !== 'wp-cloud')?.sort((a, b) => b.meta.can_migrate === true ? 1 : -1)
          this.wpCloudHostingConnections = res.data.data?.length
              ? res.data.data.filter(item => item.provider?.slug === 'wp-cloud')
              : []
        })
        .catch((error) => {
          const message = {
            heading: wTrans('failed'),
            subHeading: error.response?.data?.message || error.message,
            type: "error",
          };
          this.setNotification(message);
        })
    },
    async getUserAuth() {
      const response = await axios.get("/user-authorization");
      const responseData = response.data;
      this.teamData = responseData;
      return true;
    },
    async getVersions() {
      const response = await axios.get("/api/v2/versions");
      const responseData = response.data.data;
      this.wp_versions = responseData.versions;

      this.default_php = responseData.default.php;

      this.default_wp = responseData.default.wp;

      this.server_groups = responseData.server_groups;
      return true;
    },
    async getDefaultCoupon() {
      const response = await axios.get("/api/v1/default-coupon");
      let coupon = response.data.data.coupon;
      let coupon_banner_text = response.data.data.coupon_banner_text;
      if (
        this.user !== null &&
        Object.prototype.hasOwnProperty.call(this.user, "active_plan") &&
        this.user.active_plan.type != "free" &&
        response.data.data.coupon_only_for_free
      ) {
        coupon = {
          name: null,
          coupon_code: null,
          discount_value: null,
        };
        coupon_banner_text = null;
      }
      if (coupon.coupon_code) {
        this.coupon = coupon;
        this.coupon_applied = true;
      }
      if (coupon_banner_text) {
        this.coupon_banner_text = coupon_banner_text;
      }
      this.coupon = coupon;
      if (coupon.coupon_code) {
        this.coupon_applied = true;
      }
      return true
    },
    async initialData() {
      const response = await axios.get("/api/v2/initial-data");
      const responseData = response.data.data;

      this.wp_versions = responseData.versions.versions;

      this.default_php = responseData.versions.default.php;

      this.default_wp = responseData.versions.default.wp;
      if(!this.user?.isOwnerLegacy){ 
        this.server_groups = responseData.versions.server_groups.filter(el => !el.is_legacy);
      }else{
        this.server_groups = responseData.versions.server_groups;
      }

      this.currency = responseData.currency;

      this.generalSurveys = responseData.general_surveys ?? [];
      this.feedbackSurveys = responseData.feedback_surveys ?? [];

      this.user_live_sites_count = responseData.user_live_sites_count;

      if (this.user) {
        const { active_subscription: activeSubscription } = this.user;
        if (activeSubscription) {
          const startedAt = activeSubscription.updated_at;
          if (startedAt) {
            const startedAtDateLocal = moment.tz(startedAt, 'UTC').local()
            const currentDate = moment();
            const months = currentDate.diff(startedAtDateLocal, 'months');
            if (months >= 6) {
              this.feedbackSurveyEnabled = true;
            }
          }
        }
      }

      let coupon = responseData.default_coupon.coupon;
      let coupon_banner_text = responseData.default_coupon.coupon_banner_text;
      let coupon_banner_link = responseData.default_coupon.coupon_banner_link;
      if (
        this.teamData?.teamOwnerActivePlan?.name != "Free" &&
        responseData.default_coupon.coupon_only_for_free
      ) {
        coupon = {
          name: null,
          coupon_code: null,
          discount_value: null,
        };
        coupon_banner_text = null;
        coupon_banner_link = null;
      }
      // if (coupon.coupon_code) {
      //   this.coupon = coupon;
      //this.coupon_applied = true;
      //}
      if (coupon_banner_text) {
        this.coupon_banner_text = coupon_banner_text;
      }
      if (coupon_banner_link) {
        this.coupon_banner_link = coupon_banner_link;
      }
      // this.coupon = coupon;
      // if (coupon.coupon_code) {
      //     this.coupon_applied = true;
      // }

      if (responseData.wp_blogs) {
        // commit('setBlogs', responseData.wp_blogs.result)
        this.blogs = responseData.wp_blogs.result
      }

      if (responseData.feature_availability?.data && responseData.feature_availability?.meta) {
        this.feature_available_from = responseData.feature_availability.data
        this.plans = responseData.feature_availability.meta.plans
        this.featureAvailableFromIntervalWise = responseData.feature_availability.meta.featureAvailableFromIntervalWise
      }

      if (responseData.offers) {
        this.offers = responseData.offers
      }

      if (responseData.config_data) {
        this.configs = responseData.config_data.configs;
        this.configurationLibrary = responseData.config_data.configLibrary || null;
      }

      if(responseData.subdomain_list){
          this.domains = {}
        Object.keys(responseData.subdomain_list).forEach((key, index) => {
          this.domains[key] = '.' + responseData.subdomain_list[key];
        });
      }
      if (responseData.site_plans) {
        this.site_plans = responseData.site_plans;
        this.free_site_count = responseData.free_site_count;
        this.free_lifetime_site_count = responseData.free_lifetime_site_count;
        this.active_free_site_limit = responseData.active_free_site_limit;
        this.life_time_free_site_limit = responseData.life_time_free_site_limit;
      }
      return true;
    },
    async getEmptyState() {
      if (this.emptyStateData.length) {
        return this.emptyStateData
      }
      await axios
        .get("/api/v2/dashboard-empty-state")
        .then((response) => {
          this.emptyStateData = response.data.data;
        })
        .catch();
    },
    getUserConfigs(force = false) {
      if (!force && this.configs) {
        return Promise.resolve(this.configs);
      }
      return axios
        .get("/api/v1/configs")
        .then((response) => {
          this.configs = response.data.data.configs;
          this.configurationLibrary = response.data.data.configLibrary;
          return response.data.data;
        })
        .catch((error) => {
          console.error('Error fetching user configs:', error);
          throw error;
        });
    },
    getOnboardConfigs() {
      axios
        .get("/api/v1/onboard-configs")
        .then((response) => {
          this.configs = response.data.data.configs;
        })
        .catch();
    },
    async getPublicTemplates(filter_by_fav = false) {
      const response = await axios.get(
        `/templates/public?filter_by_fav=${filter_by_fav}`
      );
      const jsonData = response.data;
      const publicTemplates = jsonData.data;
      this.public_templates = publicTemplates
      return true;
    },
    subscribeAppVersionReleaseEvent() {
      window.Echo.channel(`app.version.release`)
        .listen('AppVersionRelease', (e) => {
          this.new_version = e.version;
        });
    },
    subscribeSiteInstallationProgressEvent(saveSiteDetailsOnLocal = false) {
      const id = this.site_task_id
      window.Echo.channel(`site.installation.progress.${id}`)
        .listen('SiteInstallationProgress', (e) => {
          const progress = Object.prototype.hasOwnProperty.call(e, 'percentage_complete') ? e.percentage_complete : 0
          this.launch_site_progress = progress;
          this.launch_site_message = {
            heading: wTrans('installation_in_progress'),
            subHeading: wTrans('installing_wordpress')
          };

          if (progress == 100) {
            if (saveSiteDetailsOnLocal) {
              localStorage.setItem('iwp_demo_site_id', this.launch_site_detail.id)
              localStorage.setItem('iwp_demo_site_url', this.launch_site_detail.wp_url)
            }
            if (Object.prototype.hasOwnProperty.call(e, 'apply_wp_config_task_id')) {
              window.Echo.leave(`site.installation.progress.${id}`);
              this.apply_wp_config_status = 'processing';
              this.install_content_message = "Configuration";
              this.install_content_status = 'processing';
              const applyWPConfigTaskID = e.apply_wp_config_task_id
              window.Echo.channel(`site.plugin.theme.installation.progress.${applyWPConfigTaskID}`)
                .listen('SitePluginThemeInstallationProgress', (e) => {
                  const progress = Object.prototype.hasOwnProperty.call(e, 'percentage_complete') ? e.percentage_complete : 0

                  if (progress == 100) {
                    this.apply_wp_config_status = 'completed';
                    window.Echo.leave(`site.plugin.theme.installation.progress.${applyWPConfigTaskID}`);
                    if (Object.prototype.hasOwnProperty.call(e, 'install_content_task_id')) {
                      this.install_content_message = "Plugin/Theme";
                      this.install_content_status = 'processing';
                      const contentTaskID = e.install_content_task_id;
                      window.Echo.channel(`site.plugin.theme.installation.progress.${contentTaskID}`)
                          .listen('SitePluginThemeInstallationProgress', (e) => {
                              const progress = Object.prototype.hasOwnProperty.call(e, 'percentage_complete') ? e.percentage_complete : 0;

                              if (progress == 100) {
                                  this.install_content_status = 'completed';
                                  window.Echo.leave(`site.plugin.theme.installation.progress.${contentTaskID}`);
                              }

                              if ( e?.status == 'error') {
                                  this.install_content_status = 'failed';
                                  window.Echo.leave(`site.plugin.theme.installation.progress.${contentTaskID}`);
                              }
                          });
                    }else{
                      this.install_content_status = 'completed';
                    }
                  }

                  if (e?.status == 'error') {
                    this.apply_wp_config_status = 'failed';
                    this.install_content_status = 'failed';
                    window.Echo.leave(`site.plugin.theme.installation.progress.${applyWPConfigTaskID}`);
                  }

                });
            }

            if (this.launch_site_detail.site_creating_mode === 'waas') {
              this.installSshPlugin(this.launch_site_detail.waas_slug, { site_id: this.launch_site_detail.id })
            }

            this.site_task_id = 0;

            this.launch_site_message = {
              heading: wTrans('your_new_wordpress_website_ready'),
              subHeading: wTrans('wp_login_credentials')
            }

            this.launch_site_detail = {
              ...this.launch_site_detail,
              wp_username: e.username,
              wp_password: e.password,
              is_reserved: e.is_reserved,
            }
            this.launch_site_status = "completed";
            this.incrementFreeSiteCount();
            window.Echo.leave(`site.installation.progress.${id}`);
            this.refresh_table = !this.refresh_table;
          }

          if ( e?.status == 'error') {
            window.Echo.leave(`site.installation.progress.${id}`);
            this.site_task_id = 0;
            this.launch_site_message = {
              heading: `Site creation has failed.`,
              subHeading: Object.prototype.hasOwnProperty.call(e, 'comment') ? e.comment : ''
            }
            this.launch_site_status = "failed";
            this.refresh_table = !this.refresh_table;
          }
        });
    },
    installSshPlugin(slug, data) {
      axios
        .post("/api/v2/wizard/" + slug + "/site/installplugin-and-config", data)
        .then((response) => {

        }).catch((error) => {
        }).finally(() => {

        });
    },
    installPluginTheme(payload) {
      const pluginSlugs = payload?.plugin_slugs && payload.plugin_slugs ? payload.plugin_slugs : null;
      const themeSlugs = payload?.theme_slugs && payload.theme_slugs ? payload.theme_slugs : null;
      const pluginUrls = payload?.plugin_urls && payload.plugin_urls ? payload.plugin_urls : null
      const themeUrls = payload?.theme_urls && payload.theme_urls ? payload.theme_urls : null;
      const postData = {}
      let initMsg = "Installing ";
      if (pluginSlugs) {
        initMsg += 'Plugin(s)'
        postData.plugin_slugs = pluginSlugs
      }
      if (pluginUrls) {
        postData.plugin_urls = pluginUrls
        if (!pluginSlugs) {
          initMsg += 'Plugin(s)'
        }
      }
      if (themeSlugs) {
        initMsg += pluginSlugs ? ', Theme(s)' : 'Theme(s)'
        postData.theme_slugs = themeSlugs
      }
      if (themeUrls) {
        postData.theme_urls = themeUrls
        if (!themeSlugs) {
          initMsg += pluginSlugs || pluginUrls ? ', Theme(s)' : 'Theme(s)'
        }
      }
      this.install_content_message = initMsg;

      this.install_content_status = 'waiting';

      axios
        .put(
          Object.keys(this.user).length
            ? "/api/v2/sites/" + payload.id + "/install-content/"
            : "/api/v2/sites/" + payload.id + "/install-content-guest/",
          postData
        )
        .then((response) => {
          this.install_content_message = response.data.data.items_text;

          this.install_content_status = 'processing';

          window.Echo.channel(`site.plugin.theme.installation.progress.${response.data.data.task_id}`)
            .listen('SitePluginThemeInstallationProgress', (e) => {
              const progress = Object.prototype.hasOwnProperty.call(e, 'percentage_complete') ? e.percentage_complete : 0

              if (progress == 100) {
                this.install_content_status = 'completed';
                window.Echo.leave(`site.plugin.theme.installation.progress.${response.data.data.task_id}`);
              }

              if ( e?.status == 'error') {
                this.install_content_status = 'failed';

                window.Echo.leave(`site.plugin.theme.installation.progress.${response.data.data.task_id}`);
              }

            });
        })
        .then((response) => {
          window.history.pushState(
            {},
            document.title,
            window.location.pathname
          );
        })
        .then((response) => {
        })
        .catch((error) => {
          this.install_content_status = 'failed';

        });
    },
    async setCommands(payload) {
      this.commands = payload
      return true
    },
    async fetchCommands() {
      if (this.commands && this.commands.length > 0) {
        return this.commands
      }
      const response = await axios.get(`/api/v2/commands`)
      this.commands = response.data.data
      return response.data.data
    },
    async fetchSiteUsage(payload) {

      const useAppStoreObject = useAppStore()
      if (payload.path != '/') {
        await useAppStoreObject.siteUsageSetLoading({ path: payload.path, status: true });
      }

      return axios
        .post(`/api/v2/sites/${payload.siteId}/fetch-usage`, { path: payload.path })
        .then((response) => {
          if (response.data.data == null) {
            // commit('setSiteUsage', {})
            this.siteUsage = {}
            return true
          }
          const data = response.data.data
          let tree = {}
          const keys = Object.keys(data)
          for (const item of keys) {
            const size = readableFileSize(data[item])
            if (size != 'NaN undefined' && item != "") {
              const obj = {
                tabName: item,
                sizeName: size,
                path: payload.path + item + '/',
                size: data[item]
              }
              if (item.includes(".") ? false : true) {
                obj.tabs = {}
              }
              tree[item] = obj
            }
          }

          tree = Object.fromEntries(
            Object.entries(tree).sort(([, a], [, b]) => b.size - a.size)
          );
          useAppStoreObject.siteUsageSetLoading({ path: payload.path, status: false }).finally(_ => {
            if (payload.path == '/') {
              this.siteUsage = tree
            } else {
              let arr = payload.path.substring(0, payload.path.length - 1).replace(/^./, "").replace(/\//g, "/tabs/");
              arr += '/tabs'
              const data = JSON.parse(JSON.stringify(this.siteUsage))
              setObjectProperty(data, arr, tree);
              this.siteUsage = data
              return true
            }
          })


        })
        .catch((error) => {

        })
        .finally(_ => {
          return true
        });
    },
    async siteUsageSetLoading(payload) {
      let arr = payload.path.substring(0, payload.path.length - 1).replace(/^./, "").replace(/\//g, "/tabs/");
      arr += '/loading'
      const data = JSON.parse(JSON.stringify(this.siteUsage))
      setObjectProperty(data, arr, payload.status);
      this.siteUsage = data
    },
    fetchUserIntegrations() {
      axios.get("/api/v2/integrations/user/dashboard")
        .then((response) => {
          this.userMailTrapIntegrations = response.data.data.mailtrapIntergrations

          this.userIntegrationResources = response.data.data.integrationResource
        }).catch((error) => {
        })
    },
    setNotification(notification) {
      if (Object.prototype.hasOwnProperty.call(notification, 'heading') && Object.prototype.hasOwnProperty.call(notification, 'subHeading') && notification.subHeading && notification.subHeading.length > 0) {
        notify({
          title: notification.heading,
          text: notification.subHeading,
          type: notification.type
        });
        this.notification = notification;
      }
    },
    setUpgradeWarning(upgrade_warning) {
      //if (this.user.active_plan.type == 'free') {
      upgrade_warning.planNames = this.featureAvailableFromIntervalWise[upgrade_warning['feature']]
      this.setUpgradeFreeToPaid(upgrade_warning)
      // } else {
      //   this.upgrade_warning = upgrade_warning
      // }
      if (upgrade_warning) {
        this.trackPaywallEvent('paywall_modal', upgrade_warning['feature'], upgrade_warning['triggerRef'])
      }
    },
    trackPaywallEvent(event, feature, triggerRef = '') {
      //
      axios.post("/api/v2/track-paywall-event", {
        'event': event,
        'feature': feature,
        'trigger_ref': triggerRef
      })
        .then((response) => {
        })
    },
    setUpgradeFreeToPaid(payload) {
      this.upgradeFreeToPaid = payload;
    },
    setPaymentSuccessModal(payload) {
      this.paymentSuccessModal = payload;
    },
    resetSiteInstallation() {
      this.notification = this.launch_site_detail = {};
      this.launch_site_status = false;
      this.launch_site_message = this.install_content_message = this.install_content_status = false;
      this.launch_site_progress = 0;
      this.site_task_id = null;
    },
    updateLaunchSiteStatus(launch_site_status) {
      this.launch_site_status = launch_site_status;
    },
    updateLaunchSiteMessage(launch_site_message) {
      this.launch_site_message = launch_site_message;
    },
    updateLaunchSiteDetail(launch_site_detail) {
      this.launch_site_detail = launch_site_detail;
    },
    toggleRefreshTemplateTable() {
      this.refresh_template_table = !this.refresh_template_table;
    },
    updateLaunchSiteCustomRedirect(launch_site_custom_redirect) {
      this.launch_site_custom_redirect = launch_site_custom_redirect;
    },
    setSiteTaskId(site_task_id) {
      this.site_task_id = site_task_id;
    },
    toggleEditCustomPlan(value) {
      this.ui.edit_custom_plan = value;
    },
    changeCouponCode(coupon_code) {
      this.coupon.coupon_code = coupon_code;
    },
    toggleCouponApplied() {
      this.coupon_applied = !this.coupon_applied;
    },
    applyCoupon(coupon) {
      this.coupon = coupon;
      this.coupon_applied = true;
    },
    removeCoupon() {
      this.coupon = {
        coupon_code: null,
        name: null,
        percentage: null,
        text: null,
      };
      this.coupon_applied = false;
    },
    setModal(modal) {
      this.modal = modal;
    },
    togglePopupInsideModal(value) {
      this.ui.popup_inside_modal = value;
    },
    toggleRefreshTable() {
      this.refresh_table = !this.refresh_table;
    },
    setPublicTemplates(public_templates) {
      this.public_templates = public_templates;
    },
    setUserTemplates(user_templates) {
      this.user_templates = user_templates;
    },
    setSiteUsage(payload) {
      this.siteUsage = payload
    },
    async getDbEditor(siteId) {
      await axios
        .get(`/api/v1/site/db-editor/${siteId}`)
        .then((response) => {
          const responseData = response.data.data;
          openWindowWithPost(responseData.db_url, {
            "auth[driver]": "server",
            "auth[server]": "",
            "auth[username]": responseData.db_username,
            "auth[password]": responseData.db_password,
            "auth[db]": responseData.db_name,
          });
          return true;
        })
        .catch((error) => {

          return false;
        })
        .finally(() => {
        });
    },
    async getCodeEditor(siteId, editor = 'wpcodebox') {
      await axios
        .get(`/api/v2/sites/${siteId}/code-editor?type=${editor}`)
        .then((response) => {
          openWindowWithPost(response.data.data.code_editor_url, {});
          return true;
        })
        .catch((error) => {

          return false;
        })
        .finally(() => {
        });
    },
    async getAccessKey(siteId, openWindow = true) {
      return await axios
        .get(`/api/v2/sites/${siteId}/access-key`)
        .then((response) => {
          if (openWindow) {
            // openWindowWithPost('vscode://instawp.instacode/instacode?access_key='+response.data.data.key, {}, "_self");
            location.href = 'vscode://instawp.instacode/instacode?access_key=' + response.data.data.key;
          }
          return response.data.data.key;
        })
        .catch((error) => {
          return false;
        })
        .finally(() => {
        });
    },
    setSelectedPlan(selectedPlan) {
      this.selectedPlan = selectedPlan
    },
    setCardIsActivated(cardIsActivated) {
      this.cardIsActivated = cardIsActivated
    },
    setMessage(message) {
      this.message = message
    },
    async fetchStackCategories() {
      if (this.stackcategoryList && this.stackcategoryList.length > 0) {
        return this.stackcategoryList
      }
      const response = await axios.get(`/api/v2/stack-categories`)
      this.stackcategoryList = response.data.data
      return response.data.data
    },
    async fetchStoreCategories() {
      if (this.storecategoryList && this.storecategoryList.length > 0) {
        return this.storecategoryList
      }
      const response = await axios.get(`/api/v2/store-categories`)
      this.storecategoryList = response.data.data
      return this.storecategoryList;
    },
    async fetchPageBuilders() {
      if (this.pageBuilders && this.pageBuilders.length > 0) {
        return this.pageBuilders
      }
      const response = await axios.get(`/api/v2/page-builders`)
      this.pageBuilders = response.data.data
      return this.pageBuilders;
    },
    async fetchSuffixDomains() {
      if (this.suffixDoamins && this.suffixDoamins.length > 0) {
        return this.suffixDoamins
      }
      const response = await axios.get(`/api/v1/suffix-domains`)
      this.suffixDoamins = response.data.data;
      return this.suffixDoamins;
    },
    addSiteOpenedEvent(resourceId, fromPage, type) {
      if (this.user && this.user.id) {
        axios.post("/api/v2/track-site-open-event", {
          'resource_id': resourceId,
          'from_page': fromPage,
          type
        })
      }
    },
    setConnectBulkSelectionToLocalStorage() {
      localStorage.setItem('connectBulkSelection', JSON.stringify(this.connectBulkSelection))
    },
    setSelectAllConnectBulkEditToLocalStorage() {
      localStorage.setItem('selectAllConnectBulkEdit', this.selectAllConnectBulkEdit)
    },
    updateGeneralSurveys(userSurvey) {
      const existingGeneralSurveyIndex = this.generalSurveys.findIndex(
        automaticSurvey => automaticSurvey.id === userSurvey.survey_id
      );

      if (existingGeneralSurveyIndex > -1) {
        const updatedSurvey = this.generalSurveys[existingGeneralSurveyIndex]
        updatedSurvey.user_survey = userSurvey;

        this.generalSurveys = uniq([...this.generalSurveys, updatedSurvey]);
      }
    },
    updateFeedbackSurveys(feedbackUserSurvey) {
      const existingFeedbackSurveyIndex = this.feedbackSurveys.findIndex(
        feedbackSurvey => feedbackSurvey.id === feedbackUserSurvey.survey_id
      );

      if (existingFeedbackSurveyIndex > -1) {
        const updatedSurvey = this.feedbackSurveys[existingFeedbackSurveyIndex]
        updatedSurvey.feedback_survey = feedbackUserSurvey;

        this.feedbackSurveys = uniq([...this.feedbackSurveys, updatedSurvey]);
      }
    },
    async toggleReserveSite(siteId) {
      return await axios
        .put(`/api/v2/sites/${siteId}/reserve-toggle`)
        .then((response) => {
          if (this.launch_site_detail.id === siteId) {
            this.updateLaunchSiteDetail({...this.launch_site_detail, is_reserved: !this.launch_site_detail.is_reserved});
          }
          return response.data;
        })
        .catch((error) => {

          return false;
        });
    },
    async fetchAIProviders() {
      return await axios
        .get(`/api/v2/ai-providers`)
        .then((response) => {
          this.aiProviders = response.data.data
          return response.data.data
        })
        .catch((error) => {
          return false;
        })
    },
    async pauseSubscription(resumeAt) {
      try {
        await axios.post('/api/v2/subscriptions/pause', {
          resumeAt,
        });
      } catch (error) {
        console.error('Error during pause request:', error.response?.data || error.message);
        throw error;
      }
    },
    async resumeSubscription() {
      try {
        await axios.post('/api/v2/subscriptions/resume');
      } catch (error) {
        console.error('Error during resume request:', error.response?.data || error.message);
        throw error;
      }
    },
    async fetchTags(teamId) {
      try {
        const response = await axios.get(`/api/v2/teams/${teamId}/tags`);
        this.tagItems = response.data.data;
      } catch (error) {
        notify({
          type: "error",
          title: "Error fetching tags",
          text: error?.response?.data?.message || "Something went wrong.",
        });
      }
    }
  },
  getters: {
    getCallback(state) {
      return state.callback;
    },
    getDefaultWpVersion: (state) => {
      return state.default_wp;
    },
    getDefaultPHPVersion: (state) => {
      return state.default_php;
    },
    teamCan: (state) => {
      return state.teamData.teamCan;
    },
    teamUsed: (state) => {
      return state.teamData.teamUsed;
    },
    teamAllow: (state) => {
      return state.teamData.teamAllow;
    },
    teamOwner: (state) => {
      return state.teamData.teamOwner;
    },
    teamOwnerActivePlan: (state) => {
      return state.teamData.teamOwnerActivePlan;
    },
    userActivePlan: (state) => {
      return state.user.active_plan;
    },
    userConfigs: (state) => {
      return state.configs;
    },
    getConfigurationLibrary: (state) => {
      return state.configurationLibrary;
    },
    shouldCouponApply: (state) => {
      return state.coupon_applied;
    },
    appliedCoupon: (state) => {
      return state.coupon;
    },
    // user: (state) => {
    //     return state.user;
    // },
    getUser: (state) => {
      return state.user;
    },
    getLaunchSiteStatus: (state) => {
      return state.launch_site_status;
    },
    getLaunchSiteMessage: (state) => {
      return state.launch_site_message;
    },
    getLaunchSiteProgress: (state) => {
      return state.launch_site_progress;
    },
    getLaunchSiteCustomRedirect: (state) => {
      return state.launch_site_custom_redirect;
    },
    getLaunchSiteDetail: (state) => {
      return state.launch_site_detail;
    },
    getcallback: (state) => {
      return state;
    },
    getRefreshTable: (state) => {
      return state.refresh_table;
    },
    getRefreshTemplateTable: (state) => {
      return state.refresh_template_table;
    },
    featureAvailableFrom: (state) => {
      return state.feature_available_from;
    },
    userBaseActivePlan: (state) => {
      return state.user.active_plan.is_custom_plan ? state.user.active_plan.base_plan : state.user.active_plan
    },
    hostingProviderFields: (state) => {
      return state.hosting_provider_meta
    },
    getInstallContentStatus: (state) => {
      return state.install_content_status;
    },
    getInstallContentMessage: (state) => {
      return state.install_content_message;
    },
    getInstallContentMsg: (state) => {
      if (!state.install_content_status) {
        return false
      }
      if (state.install_content_status == 'waiting') {
        if (state.install_content_message == 'Configuration') { 
          return 'Configuration will be applied.'
        }
        return state.install_content_message + ' will be installed.'
      } else if (state.install_content_status == 'processing') {
        if (state.install_content_message == 'Configuration') { 
          return 'Applying configuration.'
        }
        return state.install_content_message + ' installation has started.'
      } else if (state.install_content_status == 'completed') {
        if (state.install_content_message == 'Configuration') { 
          return 'Configuration applied.'
        }
        return state.install_content_message + ' installed.'
      } else if (state.install_content_status == 'failed') {
        if (state.install_content_message == 'Configuration') { 
          return 'Configuration failed.'
        }
        return state.install_content_message + ' installation failed.'
      } else {
        return false
      }
    },
    serverGroups: (state) => {
      return state.server_groups
    },
    getNewVersion: (state) => {
      return state.new_version;
    },
    getSiteTaskId: (state) => {
      return state.site_task_id;
    },
    getCommands: (state) => {
      return state.commands
    },
    getBlogs: (state) => {
      return state.blogs
    },
    getSiteUsage: (state) => {
      return state.siteUsage
    },
    getUserMailTrapIntegrations: (state) => {
      return state.userMailTrapIntegrations
    },
    getUserIntegrationResources: (state) => {
      return state.userIntegrationResources
    },
    getSelectedPlan: (state) => {
      return state.selectedPlan
    },
    getCardIsActivated: (state) => {
      return state.cardIsActivated
    },
    getMessage: (state) => {
      return state.message
    },
    isUserPartner: (state) => {
      if (!state.user) {
        return false
      }
      return state.user.is_partner ? true : false;
    },
    getPopularPlugins: (state) => {
      if (state.stackcategoryList.length == 0) {
        return []
      }
      const popularCat = state.stackcategoryList.find(item => item.name === 'Popular')
      if (!popularCat) {
        return []
      }
      const products = JSON.parse(JSON.stringify(popularCat.products))
      for (const product of products) {
        const categoryNameList = []
        for (const categoryId of product.category_ids) {
          const cat = state.stackcategoryList.find(item => item.name !== 'Popular' && item.id == categoryId)
          if (cat) {
            categoryNameList.push(cat.name)
          }
        }
        product.categoryNameList = categoryNameList
      }
      return products
    },
    isTenantTypeInstaWP: (stage) => {
      if (!stage.tenant) {
        return false
      }
      return stage.tenant.type == 'instawp' ? true : false
    },
    isTenantTypeExternal: (stage) => {
      if (!stage.tenant) {
        return false
      }
      return stage.tenant.type == 'external' ? true : false
    },
    hasAdminRole: (state) => {
      if (!state.user) {
        return false
      }
      return state.user.hasAdminRole;
    },
    allHostingConnections: (state) => {
        if (state.wpCloudHostingConnections?.length || state.hostingConnections?.length) {
            return [...state.wpCloudHostingConnections, ...state.hostingConnections]
        } else {
            return []
        }
    },
  },
  share: {
    // An array of fields that the plugin will ignore.
    omit: [
      'launch_site_status',
      'launch_site_message',
      'launch_site_progress',
      'launch_site_detail',
      'launch_site_custom_redirect',
      'configs',
      'upgradeFreeToPaid',
      'paymentSuccessModal',
      'connectBulkSelection',
      'site'
      // 'callback'
    ],
    // Override global config for this store.
    enable: true,
    initialize: true,
  },
  strict: process.env.NODE_ENV !== "production",
});
